import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FaPlay, FaFilePdf, FaFileWord, FaFileAlt, FaFileImage, FaFileVideo, FaGlobe, FaLightbulb, FaGraduationCap } from 'react-icons/fa';
import { initLeadTracking, trackPageView, trackCTAClick } from '../../utils/leadTracking';
import { DEMO_VIDEO_ID } from '../../constants';

function demoEmbedSrc({ autoplay = false } = {}) {
  const params = new URLSearchParams({
    rel: '0',
    modestbranding: '1',
    iv_load_policy: '3',
    playsinline: '1',
    enablejsapi: '1',
  });
  if (autoplay) params.set('autoplay', '1');
  return `https://www.youtube-nocookie.com/embed/${DEMO_VIDEO_ID}?${params}`;
}

/** Poster first, iframe on play — keeps YouTube chrome off the page until the user opts in. */
function HeroDemoVideo({ t, onPlayTrack }) {
  const [active, setActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [posterQuality, setPosterQuality] = useState('maxres');

  const posterSrc = useMemo(
    () => (posterQuality === 'maxres'
      ? `https://i.ytimg.com/vi/${DEMO_VIDEO_ID}/maxresdefault.jpg`
      : `https://i.ytimg.com/vi/${DEMO_VIDEO_ID}/hqdefault.jpg`),
    [posterQuality],
  );

  const handlePlay = useCallback(() => {
    onPlayTrack?.();
    setError(false);
    setLoading(true);
    setActive(true);
  }, [onPlayTrack]);

  const handleRetry = useCallback(() => {
    setError(false);
    setLoading(true);
    setActive(true);
  }, []);

  return (
    <div className="relative w-full pt-[56.25%] bg-slate-950">
      {!active && !error && (
        <button
          type="button"
          onClick={handlePlay}
          className="group absolute inset-0 w-full h-full overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
          aria-label={t('landing.slideOne.hero.demoVideo.play', 'Play product demo')}
        >
          <img
            src={posterSrc}
            alt=""
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
            loading="eager"
            decoding="async"
            onError={() => setPosterQuality('hq')}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/50 via-slate-950/10 to-slate-950/20" aria-hidden />
          <span className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <span className="flex h-16 w-16 sm:h-[4.5rem] sm:w-[4.5rem] items-center justify-center rounded-full bg-white/95 text-blue-600 shadow-xl shadow-black/25 ring-4 ring-white/30 transition-transform duration-300 group-hover:scale-110 group-active:scale-95">
              <FaPlay className="ms-1 text-xl sm:text-2xl" aria-hidden />
            </span>
            <span className="text-sm font-semibold text-white/90 drop-shadow-md">
              {t('landing.slideOne.hero.demoVideo.seeIt', 'See it in action')}
            </span>
          </span>
        </button>
      )}

      {active && !error && (
        <div className="absolute inset-0 overflow-hidden">
          {loading && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-slate-950 text-white">
              <div className="mb-3 h-10 w-10 animate-spin rounded-full border-2 border-white/20 border-t-white" />
              <p className="text-sm font-medium">{t('landing.slideOne.hero.demoVideo.loading', 'Loading...')}</p>
            </div>
          )}
          <iframe
            src={demoEmbedSrc({ autoplay: true })}
            title={t('landing.slideOne.hero.demoVideo.title', 'Product Demo')}
            className="absolute left-1/2 top-1/2 h-[103%] w-[103%] -translate-x-1/2 -translate-y-1/2 border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            onLoad={() => setLoading(false)}
            onError={() => { setError(true); setLoading(false); }}
          />
        </div>
      )}

      {error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900 p-8 text-center text-white">
          <p className="mb-4 text-sm opacity-90">{t('landing.slideOne.hero.demoVideo.unavailable')}</p>
          <motion.button
            type="button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleRetry}
            className="rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-slate-900"
          >
            {t('landing.slideOne.hero.demoVideo.retry')}
          </motion.button>
        </div>
      )}
    </div>
  );
}

const SlideOne = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const uiLang = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
  const useCompactHeroTitle = uiLang === 'fr' || uiLang === 'nl';

  useEffect(() => {
    initLeadTracking();
    trackPageView('landing', { section: 'slideOne' });
  }, []);

  const handleDemoPlay = useCallback(() => {
    trackCTAClick('demo_video_play', 'landing_hero');
  }, []);

  const sourceFormats = useMemo(() => [
    { Icon: FaFilePdf, label: 'PDF', iconClass: 'text-red-500', chipClass: 'border-red-200/80 bg-red-50/80 dark:border-red-900/50 dark:bg-red-950/30' },
    { Icon: FaFileWord, label: 'DOCX', iconClass: 'text-blue-600', chipClass: 'border-blue-200/80 bg-blue-50/80 dark:border-blue-900/50 dark:bg-blue-950/30' },
    { Icon: FaFileAlt, label: 'TXT', iconClass: 'text-slate-500', chipClass: 'border-slate-200/80 bg-slate-50/80 dark:border-slate-700/50 dark:bg-slate-800/50' },
    { Icon: FaFileImage, label: 'PNG', iconClass: 'text-violet-500', chipClass: 'border-violet-200/80 bg-violet-50/80 dark:border-violet-900/50 dark:bg-violet-950/30' },
    { Icon: FaFileVideo, label: 'MP4', iconClass: 'text-rose-500', chipClass: 'border-rose-200/80 bg-rose-50/80 dark:border-rose-900/50 dark:bg-rose-950/30' },
    { Icon: FaGlobe, label: t('landing.slideOne.sourceFormats.links', 'Links'), iconClass: 'text-indigo-500', chipClass: 'border-indigo-200/80 bg-indigo-50/80 dark:border-indigo-900/50 dark:bg-indigo-950/30' },
  ], [t]);

  const contentTypes = [
    { icon: '📁', label: t('landing.slideOne.contentTypes.knowledgeProjects', 'Knowledge Projects'), color: 'from-blue-400 to-blue-500' },
    { icon: '🖥️', label: t('landing.slideOne.contentTypes.presentations', 'AI Presentations'), color: 'from-sky-400 to-sky-500' },
    { icon: '🤖', label: t('landing.slideOne.contentTypes.aiTutor', 'AI Tutor'), color: 'from-indigo-400 to-indigo-500' },
    { icon: '✨', label: t('landing.slideOne.contentTypes.interactiveLessons', 'Interactive lessons'), color: 'from-amber-400 to-amber-500' },
  ];

  return (
    <main className="w-full bg-white dark:bg-slate-950">
      {/* Hero Section - Duolingo Style */}
      <section className="relative overflow-hidden pt-14 pb-10 sm:pt-16 sm:pb-12 md:pt-20 md:pb-14 lg:pt-20 lg:pb-16">
        {/* Playful Duolingo-style background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-yellow-50/30 to-indigo-50 dark:from-gray-950 dark:via-blue-950/10 dark:to-gray-950" />
        <div className="absolute top-10 right-10 w-64 h-64 bg-yellow-200/40 dark:bg-yellow-900/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-10 left-10 w-80 h-80 bg-blue-200/30 dark:bg-blue-900/15 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-indigo-100/20 dark:bg-indigo-900/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-10 xl:gap-12 items-center">
            {/* Left: Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="space-y-4 min-w-0 w-full"
            >
              <div className="space-y-4 w-full min-w-0">
                <h1
                  className={[
                    'font-bold text-slate-900 dark:text-slate-50 tracking-tight text-pretty',
                    useCompactHeroTitle
                      ? 'text-[1.625rem] sm:text-[1.75rem] md:text-[1.875rem] lg:text-[2.125rem] xl:text-[2.25rem] leading-[1.4] sm:leading-[1.45] whitespace-pre-line'
                      : 'text-[1.75rem] sm:text-[1.875rem] md:text-[2.125rem] lg:text-[2.5rem] xl:text-[2.625rem] leading-[1.28] sm:leading-[1.32]',
                  ].join(' ')}
                >
                  {t('landing.slideOne.hero.title', 'Turn your knowledge into complete learning experiences')}
                </h1>

                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08, duration: 0.4 }}
                  className="inline-flex max-w-xl items-start gap-2.5 rounded-xl border border-amber-200/90 bg-gradient-to-r from-amber-50 to-yellow-50/80 px-3 py-2.5 shadow-sm dark:border-amber-800/50 dark:from-amber-950/40 dark:to-yellow-950/20"
                >
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-amber-500 text-white shadow-sm">
                    <FaLightbulb className="h-3.5 w-3.5" aria-hidden />
                  </span>
                  <p className="text-[12px] sm:text-[13px] leading-snug text-slate-700 dark:text-amber-100/90">
                    <span className="font-bold text-amber-800 dark:text-amber-300">
                      {t('landing.slideOne.hero.topicWidget.or', 'Or')}
                    </span>
                    {' '}
                    {t(
                      'landing.slideOne.hero.topicWidget.body',
                      'just type the topics you want to learn. Our academic AI builds the full lesson in the format you prefer.',
                    )}
                  </p>
                  <FaGraduationCap className="mt-0.5 h-4 w-4 shrink-0 text-amber-600/70 dark:text-amber-400/70" aria-hidden />
                </motion.div>

                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 pt-0.5">
                  {sourceFormats.map(({ Icon, label, iconClass, chipClass }, i) => (
                    <motion.div
                      key={label}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.12 + i * 0.04, type: 'spring', stiffness: 260 }}
                      className={`flex items-center gap-1 rounded-md border px-2 py-1 ${chipClass}`}
                    >
                      <Icon className={`h-3.5 w-3.5 shrink-0 ${iconClass}`} aria-hidden />
                      <span className="text-[11px] font-medium text-slate-600 dark:text-slate-300">{label}</span>
                    </motion.div>
                  ))}
                </div>

                <p className="text-sm sm:text-[0.9375rem] lg:text-base text-slate-600 dark:text-slate-400 leading-relaxed max-w-prose pt-0.5">
                  {t(
                    'landing.slideOne.hero.description',
                    'Upload documents, presentations, websites, or videos, or start from a topic alone. Coursolo builds a full structured course: modules, interactive lessons, AI Presentations, quizzes, labs, and a grounded AI tutor.',
                  )}
                </p>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-2.5 pt-0.5 w-full sm:w-auto">
                <motion.button
                  whileHover={{ scale: 1.03, y: -2 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => {
                    trackCTAClick('signup', 'landing_hero');
                    navigate('/signup');
                  }}
                  className="w-full sm:w-auto px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-2xl transition-all shadow-lg shadow-blue-600/25 text-sm"
                >
                  {t('landing.cta.startFree', 'Start free')}
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.03, y: -2 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => {
                    trackCTAClick('course_ideas', 'landing_hero');
                    navigate('/course-ideas');
                  }}
                  className="w-full sm:w-auto px-6 py-3 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-900 dark:text-slate-50 font-semibold rounded-xl border border-slate-300 dark:border-slate-600 hover:border-blue-500 dark:hover:border-blue-400 transition-all text-sm shadow-sm"
                >
                  {t('landing.slideOne.hero.courseIdeasHub', 'Explore Ideas')}
                </motion.button>
              </div>
            </motion.div>

            {/* Right: Video */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="relative min-w-0 w-full"
            >
              <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-slate-950 shadow-2xl shadow-slate-900/20 dark:border-slate-700/80 dark:shadow-black/40">
                <HeroDemoVideo t={t} onPlayTrack={handleDemoPlay} />
              </div>

              {/* Content Types - Duolingo colorful badges */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mt-4 sm:mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2"
              >
                {contentTypes.map((type, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0.9, rotate: -5 }}
                    animate={{ opacity: 1, scale: 1, rotate: 0 }}
                    transition={{ delay: 0.6 + i * 0.05, type: 'spring', stiffness: 200 }}
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    className="flex flex-col items-center gap-1.5 p-3 bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-2xl border-2 border-slate-200 dark:border-slate-700 hover:border-blue-400 dark:hover:border-blue-500 shadow-md hover:shadow-xl transition-all"
                  >
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${type.color} flex items-center justify-center text-2xl shadow-lg`}>
                      {type.icon}
                    </div>
                    <span className="text-xs font-bold text-gray-700 dark:text-gray-300 text-center">{type.label}</span>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What is Coursolo — problem / audience / solution */}
      <section className="py-12 md:py-14 bg-gradient-to-b from-slate-50 to-white dark:from-slate-900/50 dark:to-slate-950 border-y border-slate-200/80 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 dark:text-slate-50 mb-3">
              {t('landing.slideOne.whatIs.title', 'What is Coursolo?')}
            </h2>
            <p className="text-base md:text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
              {t('landing.slideOne.whatIs.body', 'An AI learning studio that turns your topics and documents into structured courses, with interactive lessons, not generic chat answers.')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: t('landing.slideOne.clarity.problem.title', 'Problem'), body: t('landing.slideOne.clarity.problem.body'), icon: '⚠️' },
              { label: t('landing.slideOne.clarity.for.title', 'For'), body: t('landing.slideOne.clarity.for.body'), icon: '🎯' },
              { label: t('landing.slideOne.clarity.why.title', 'Why now'), body: t('landing.slideOne.clarity.why.body'), icon: '⏱️' },
              { label: t('landing.slideOne.clarity.how.title', 'How Coursolo helps'), body: t('landing.slideOne.clarity.how.body'), icon: '✨' },
            ].map((card, i) => (
              <motion.div
                key={card.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="p-5 rounded-2xl bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-700"
              >
                <span className="text-2xl mb-2 block" aria-hidden>{card.icon}</span>
                <p className="text-xs font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400 mb-2">{card.label}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{card.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section - Reduced spacing */}
      <section className="py-12 md:py-16 bg-white dark:bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 dark:text-slate-50 mb-3 tracking-tight">
              {t('landing.slideOne.features.title', 'Everything you need to succeed')}
            </h2>
            <p className="text-lg md:text-base sm:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              {t('landing.slideOne.researchNote', 'Powerful features that make learning engaging, effective, and enjoyable')}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { emoji: '📁', title: t('landing.slideOne.features.knowledgeProjects.title', 'Knowledge Projects'), desc: t('landing.slideOne.features.knowledgeProjects.description', 'Upload PDFs and docs. AI builds grounded courses from your materials.'), color: 'from-blue-400 to-blue-500' },
              { emoji: '✨', title: t('landing.slideOne.features.interactiveLessons.title', 'Interactive GenUI Lessons'), desc: t('landing.slideOne.features.interactiveLessons.description', 'Quizzes, labs, step guides, and canvases, not just text walls'), color: 'from-indigo-400 to-indigo-500' },
              { emoji: '🖥️', title: t('landing.slideOne.features.presentations.title', 'AI Presentations'), desc: t('landing.slideOne.features.presentations.description', 'Multi-agent teaching decks with diagrams, quizzes, and editable slides'), color: 'from-sky-400 to-sky-500' },
              { emoji: '🤖', title: t('landing.slideOne.features.smartAssistant.title', 'Grounded AI Tutor'), desc: t('landing.slideOne.features.smartAssistant.description', 'Ask questions while learning. Answers are grounded in your sources.'), color: 'from-emerald-400 to-emerald-500' },
              { emoji: '🌐', title: t('landing.slideOne.features.globalLearning.title', '8 Course Languages'), desc: t('landing.slideOne.features.globalLearning.description', 'Generate full courses and decks in EN, FR, ES, DE, IT, PT, NL, AR'), color: 'from-amber-400 to-amber-500' },
              { emoji: '📊', title: t('landing.slideOne.features.analytics.title', 'Assessments & Progress'), desc: t('landing.slideOne.features.analytics.description', 'Quizzes, rubrics, reflections, and progress you can actually prove'), color: 'from-rose-400 to-rose-500' },
            ].map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.1, duration: 0.6 }}
                whileHover={{ y: -5, scale: 1.02 }}
                className="group p-6 bg-white dark:bg-slate-900 rounded-2xl border-2 border-slate-200 dark:border-slate-700 hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-xl transition-all duration-300"
              >
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${f.color} flex items-center justify-center text-3xl mb-4 shadow-lg`}>
                  {f.emoji}
                </div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-slate-50 mb-2">{f.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default SlideOne;
